import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { exclusionsQuery, importOverrides, putExclusions } from '@/api/index'
import { keys } from '@/api/keys'
import { Button } from '@/components/ui/button'
import { Field, Textarea } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import type { IndexExclusions } from '@/schema'

/**
 * What the crawler is allowed to look at (§5.4 `/api/index/exclusions`).
 *
 * Three lists, one per line, because that is how an operator writes them —
 * no JSON, no chips to fight with. `roots` is an allow-list of top-level
 * prefixes; the two pattern lists are regexes matched against directory
 * names and full keys.
 *
 * Getting these wrong is the difference between a 4-minute index and a
 * 40-minute one, so the help text says what each list does in one sentence.
 */

type ListKey = keyof IndexExclusions

const FIELDS: { key: ListKey; label: string; hint: string; placeholder: string }[] = [
  {
    key: 'roots',
    label: 'Folders to index',
    hint: 'Top-level prefixes in the bucket. Everything outside them is ignored completely.',
    placeholder: 'backgrounds/\nlive_backgrounds/\nsprites/\nobjects/\naudio/',
  },
  {
    key: 'dir_patterns',
    label: 'Folder names to skip',
    hint: 'Regular expressions matched against a single folder name — .source, trash, backups.',
    placeholder: '^\\.source$\n^trash$\n\\.bak$',
  },
  {
    key: 'key_patterns',
    label: 'File paths to skip',
    hint: 'Regular expressions matched against the whole key.',
    placeholder: '\\.DS_Store$\n/Thumbs\\.db$',
  },
]

function toText(list: string[] | undefined): string {
  return (list ?? []).join('\n')
}

function fromText(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** A bad regex here would fail the whole index run, so it is caught in the form. */
function badPatterns(list: string[]): string[] {
  return list.filter((p) => {
    try {
      new RegExp(p)
      return false
    } catch {
      return true
    }
  })
}

export function ExclusionsForm() {
  const qc = useQueryClient()
  const query = useQuery(exclusionsQuery)
  const [draft, setDraft] = useState<Record<ListKey, string> | null>(null)

  useEffect(() => {
    if (query.data) {
      setDraft({ roots: toText(query.data.roots), dir_patterns: toText(query.data.dir_patterns), key_patterns: toText(query.data.key_patterns) })
    }
  }, [query.data])

  const save = useMutation({
    mutationFn: (d: Record<ListKey, string>) =>
      putExclusions({ roots: fromText(d.roots), dir_patterns: fromText(d.dir_patterns), key_patterns: fromText(d.key_patterns) }),
    onSuccess: (next) => {
      qc.setQueryData(keys.index.exclusions(), next)
      toast.success('Saved', { description: 'The next index run uses these.' })
    },
    onError: (e) => toast.error('Could not save', { description: e instanceof Error ? e.message : String(e) }),
  })

  const overrides = useMutation({
    mutationFn: importOverrides,
    onSuccess: (r) => {
      toast.success('Importing overrides', { description: `Job #${r.job_id} is reading manifests/asset_overrides.json.` })
      void qc.invalidateQueries({ queryKey: keys.jobs.all() })
    },
    onError: (e) => toast.error('Could not start the import', { description: e instanceof Error ? e.message : String(e) }),
  })

  if (query.isPending || !draft) {
    return (
      <section className="card p-5">
        <p className="text-sm text-ink-2">Loading…</p>
      </section>
    )
  }
  if (query.isError) {
    return (
      <section className="card p-5" role="alert">
        <p className="text-sm text-ink-2">Could not load the exclusions. {query.error instanceof Error ? query.error.message : ''}</p>
      </section>
    )
  }

  const invalid = [...badPatterns(fromText(draft.dir_patterns)), ...badPatterns(fromText(draft.key_patterns))]

  return (
    <section className="card p-5" aria-labelledby="exclusions-heading">
      <h2 id="exclusions-heading" className="text-lg font-semibold text-ink">
        What to index
      </h2>
      <p className="mt-1 text-sm text-ink-2">One entry per line. Changes apply to the next index run, not to what is already stored.</p>

      <div className="mt-4 flex flex-col gap-4">
        {FIELDS.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>
            <Textarea
              rows={f.key === 'roots' ? 5 : 3}
              value={draft[f.key]}
              placeholder={f.placeholder}
              spellCheck={false}
              onChange={(e) => setDraft((d) => (d ? { ...d, [f.key]: e.target.value } : d))}
              aria-label={f.label}
              className="font-mono text-xs"
            />
          </Field>
        ))}
      </div>

      {invalid.length > 0 ? (
        <p className="mt-3 rounded-xl border border-danger bg-danger-soft px-3 py-2 text-sm text-ink" role="alert">
          {invalid.length === 1 ? 'This pattern is not a valid regular expression: ' : 'These patterns are not valid regular expressions: '}
          <code className="font-mono">{invalid.join(', ')}</code>
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={invalid.length > 0}>
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={() =>
            query.data && setDraft({ roots: toText(query.data.roots), dir_patterns: toText(query.data.dir_patterns), key_patterns: toText(query.data.key_patterns) })
          }
        >
          Undo changes
        </Button>
        <Button variant="ghost" className="ml-auto" onClick={() => overrides.mutate()} loading={overrides.isPending}>
          Import asset overrides
        </Button>
      </div>
    </section>
  )
}
